'use client';

import { useState, useEffect, useCallback, type RefObject } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface ScrollIndicatorProps {
  /** Ref to the scrollable container to monitor */
  containerRef: RefObject<HTMLElement | null>;
  /** Optional ref to the content boundary element. When provided, the indicator
   *  hides when this element's bottom is visible (e.g., to stop before a footer). */
  contentRef?: RefObject<HTMLElement | null>;
  /** Visual variant - 'light' for light backgrounds, 'dark' for dark backgrounds */
  variant?: 'light' | 'dark';
  /** @deprecated The indicator is a floating glass pill now — there is no
   *  gradient overlay. Accepted so existing call sites keep compiling. */
  gradientClass?: string;
  /** Whether the indicator should be active (e.g., when parent is open) */
  active?: boolean;
  /** Condensed mode - just the animated chevron pill, no text */
  condensed?: boolean;
  /** Additional class names */
  className?: string;
}

/** Double-chevron glyph — two stacked chevrons reading "keep going". */
function DoubleChevron() {
  return (
    <motion.span
      aria-hidden
      animate={{ y: [0, 2, 0] }}
      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
      className="flex flex-col items-center"
    >
      <svg
        width="10"
        height="14"
        viewBox="0 0 24 28"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ opacity: 0.4 }}
      >
        <path d="M6 5l6 6 6-6" />
        <path d="M6 17l6 6 6-6" />
      </svg>
    </motion.span>
  );
}

/**
 * Floating "Scroll for more" pill — a frosted-glass chip hovering near the
 * bottom of a scrollable container. No gradient wash over the content; the
 * pill is the whole affordance. Clicking it jumps to the next `<section>`
 * (or a page's worth of scroll when there are no sections). It hides once
 * the user nears the bottom and never renders when content already fits.
 *
 * The full pill is desktop-only — on touch screens scrolling needs no hint
 * and the pill would sit over content. The `condensed` chevron-only pill
 * (used in sheets) stays on all sizes.
 *
 * @example
 * ```tsx
 * const scrollRef = useRef<HTMLDivElement>(null);
 *
 * <div ref={scrollRef} className="overflow-y-auto">
 *   {content}
 * </div>
 * <ScrollIndicator containerRef={scrollRef} variant="dark" active={isOpen} />
 * ```
 */
export function ScrollIndicator({
  containerRef,
  contentRef,
  variant = 'light',
  active = true,
  condensed = false,
  className = '',
}: ScrollIndicatorProps) {
  const [showIndicator, setShowIndicator] = useState(false);

  const checkScrollIndicator = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    // When a contentRef is provided, use its bottom edge as the boundary
    // instead of the scroll container's full scrollHeight. This lets the
    // indicator hide when the content area ends (e.g., before a footer).
    if (contentRef?.current) {
      const contentRect = contentRef.current.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const contentBelowFold = contentRect.bottom > containerRect.bottom + 20;
      setShowIndicator(contentBelowFold);
      return;
    }

    const hasMoreContent = container.scrollHeight > container.clientHeight + 20;
    // Generous near-bottom threshold — the pill bows out as the reader
    // approaches the end rather than clinging to the last pixel.
    const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 200;

    setShowIndicator(hasMoreContent && !nearBottom);
  }, [containerRef, contentRef]);

  // Jump to the next <section> inside the container (portfolio behavior);
  // fall back to a page's worth of scroll so the click never dead-ends.
  const scrollToNext = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerTop = container.getBoundingClientRect().top;
    const sections = container.querySelectorAll('section, [data-scroll-section]');
    for (const el of sections) {
      const delta = el.getBoundingClientRect().top - containerTop;
      if (delta > 80) {
        container.scrollBy({ top: delta - 16, behavior: 'smooth' });
        return;
      }
    }
    container.scrollBy({ top: container.clientHeight * 0.8, behavior: 'smooth' });
  }, [containerRef]);

  useEffect(() => {
    if (!active) {
      setShowIndicator(false);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    // Check initially
    checkScrollIndicator();

    // Check after delays to account for content rendering and animations
    const timeouts = [
      setTimeout(checkScrollIndicator, 50),
      setTimeout(checkScrollIndicator, 150),
      setTimeout(checkScrollIndicator, 300),
      setTimeout(checkScrollIndicator, 500), // Extra check for longer animations
    ];

    // Listen for scroll events
    container.addEventListener('scroll', checkScrollIndicator);
    window.addEventListener('resize', checkScrollIndicator);

    // Use ResizeObserver to detect content size changes
    const resizeObserver = new ResizeObserver(checkScrollIndicator);
    resizeObserver.observe(container);
    if (container.firstElementChild) {
      resizeObserver.observe(container.firstElementChild);
    }

    // Use MutationObserver to catch DOM changes (sections expanding/collapsing)
    const mutationObserver = new MutationObserver(() => {
      // Debounce slightly to let animations settle
      setTimeout(checkScrollIndicator, 50);
    });
    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      timeouts.forEach(clearTimeout);
      container.removeEventListener('scroll', checkScrollIndicator);
      window.removeEventListener('resize', checkScrollIndicator);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [active, checkScrollIndicator, containerRef]);

  // Deliberately faint — a whisper of a pill, not a floating button.
  const pillTheme =
    variant === 'dark'
      ? 'border-white/5 bg-white/5 text-white/40 hover:text-white/80'
      : 'border-border/30 bg-background/30 text-muted-foreground/70 hover:text-foreground';

  return (
    <AnimatePresence>
      {showIndicator && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className={`pointer-events-none absolute right-0 bottom-0 left-0 z-10 flex justify-center ${
            condensed ? '' : 'max-md:hidden'
          } ${className}`}
        >
          <button
            type="button"
            onClick={scrollToNext}
            aria-label="Scroll for more"
            className={`pointer-events-auto mb-3 flex cursor-pointer items-center rounded-full border shadow-[0_1px_6px_rgba(0,0,0,0.06)] backdrop-blur-md transition-colors ${
              condensed ? 'gap-0 px-3 py-1.5' : 'gap-2 px-5 py-2.5'
            } ${pillTheme}`}
          >
            <DoubleChevron />
            {!condensed && (
              <>
                <span className="text-[10px] font-medium tracking-[0.15em] uppercase">
                  Scroll for more
                </span>
                <DoubleChevron />
              </>
            )}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
